/**
 * FilterableDataTable — a generic drop-in for any tabular dataset coming
 * from /api/admin/reports/{type}. Auto-detects columns from the first
 * row's keys, adds a per-column filter input (text OR numeric range OR
 * date range based on detected type), and per-column 3-state sort
 * (DESC → ASC → off).
 *
 * Drop-in usage:
 *   <FilterableDataTable
 *     rows={data.rows}
 *     repeatSidSet={lateSummary?.repeatSidSet}   // optional
 *     repeatBadgeTitle="Late 5+ times"           // optional
 *     emptyMessage="No rows for selected filters"
 *   />
 *
 * Why a generic table?
 *   • Reports has 12 tabs (attendance, late_comers, absent_leave, claim,
 *     permissions, …). Pre-Feb 2026 only the late-comers repeat-offender
 *     mini-card had filter+sort. Users on production asked for the same
 *     affordance on the main row table because they routinely scroll
 *     hundreds-of-rows just to confirm one staff's specific date.
 *   • Per-row hover + repeat badge is preserved (uses repeatSidSet).
 *
 * Column-type detection (per column, run once on mount-of-rows):
 *   • date    — every non-empty value matches YYYY-MM-DD or contains 'T'
 *                + ISO-shape (date-time strings collapse to date).
 *   • number  — every non-empty value parses to a finite number.
 *   • text    — anything else (default).
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

const detectColumnType = (rows, col) => {
  let sawValue = false;
  let allDate = true;
  let allNumber = true;
  for (const r of rows) {
    const v = r[col];
    if (v === null || v === undefined || v === '') continue;
    sawValue = true;
    const s = String(v);
    if (!ISO_DATE_RE.test(s)) allDate = false;
    const n = Number(s);
    if (!Number.isFinite(n)) allNumber = false;
    if (!allDate && !allNumber) break;
  }
  if (!sawValue) return 'text';
  if (allDate) return 'date';
  if (allNumber) return 'number';
  return 'text';
};

export default function FilterableDataTable({
  rows = [],
  repeatSidSet,
  repeatBadgeTitle = 'Repeat offender',
  emptyMessage = 'No rows for selected filters',
  testIdPrefix = 'report',
  onFilteredRowsChange,
}) {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];

  const colTypes = useMemo(() => {
    const map = {};
    for (const h of headers) map[h] = detectColumnType(rows, h);
    return map;
  }, [rows, headers]);

  // filters: { [colKey]: 'text' | {min,max} | {dateMin,dateMax} }
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState({ key: null, dir: null });

  const filtered = useMemo(() => {
    const text = {};
    for (const [k, v] of Object.entries(filters)) {
      if (typeof v === 'string' && v !== '') text[k] = v.toLowerCase();
    }
    const inRange = (val, min, max) => {
      if (val === null || val === undefined || val === '') return min === '' && max === '';
      const n = Number(val);
      if (!Number.isFinite(n)) return false;
      if (min !== '' && min !== undefined && n < Number(min)) return false;
      if (max !== '' && max !== undefined && n > Number(max)) return false;
      return true;
    };
    const inDateRange = (val, min, max) => {
      const s = String(val || '').slice(0, 10);
      if (min && (!s || s < min)) return false;
      if (max && s && s > max) return false;
      return true;
    };
    return rows.filter(row => {
      for (const h of headers) {
        const t = colTypes[h];
        const f = filters[h];
        if (!f) continue;
        if (t === 'text') {
          if (typeof f !== 'string' || f === '') continue;
          if (!String(row[h] ?? '').toLowerCase().includes(f.toLowerCase())) return false;
        } else if (t === 'number') {
          const min = f.min ?? '';
          const max = f.max ?? '';
          if (min === '' && max === '') continue;
          if (!inRange(row[h], min, max)) return false;
        } else if (t === 'date') {
          const min = f.dateMin ?? '';
          const max = f.dateMax ?? '';
          if (!min && !max) continue;
          if (!inDateRange(row[h], min, max)) return false;
        }
      }
      return true;
    });
  }, [rows, headers, colTypes, filters]);

  const sorted = useMemo(() => {
    if (!sortBy.key) return filtered;
    const sign = sortBy.dir === 'asc' ? 1 : -1;
    const t = colTypes[sortBy.key];
    return [...filtered].sort((a, b) => {
      const av = a[sortBy.key];
      const bv = b[sortBy.key];
      if (t === 'number') return (Number(av || 0) - Number(bv || 0)) * sign;
      // date columns sort lex (ISO format ⇒ same ordering as chronological)
      return String(av ?? '').localeCompare(String(bv ?? '')) * sign;
    });
  }, [filtered, sortBy, colTypes]);

  const handleSort = (key) => {
    setSortBy(prev => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };
    });
  };

  const setColFilter = (key, patch) => {
    setFilters(prev => {
      const next = { ...prev };
      if (typeof patch === 'string') {
        if (patch === '') delete next[key]; else next[key] = patch;
      } else {
        const cur = (typeof next[key] === 'object' && next[key]) || {};
        const merged = { ...cur, ...patch };
        // Drop the entry entirely when both legs of the range cleared.
        if (Object.values(merged).every(v => v === '' || v === undefined)) {
          delete next[key];
        } else {
          next[key] = merged;
        }
      }
      return next;
    });
  };

  const clearFilters = () => setFilters({});
  const hasAnyFilter = Object.keys(filters).length > 0;

  // Bubble the *post-filter post-sort* rows up to the parent so the
  // Download-Excel button can export exactly what the admin sees.
  // (June 18, 2026 — user ask: "make Excel respect my filters")
  useEffect(() => {
    if (onFilteredRowsChange) onFilteredRowsChange(sorted);
  }, [sorted, onFilteredRowsChange]);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`${testIdPrefix}-table`}>
      <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2 text-xs">
        <span className="text-gray-600">
          Showing <b>{sorted.length}</b> of <b>{rows.length}</b> rows
        </span>
        {hasAnyFilter && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2 py-1 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 rounded text-[11px] font-medium"
            data-testid={`${testIdPrefix}-clear-filters`}
          >
            <X size={11} /> Clear filters
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {headers.map(h => (
                <SortHeader
                  key={h} label={h}
                  isActive={sortBy.key === h}
                  dir={sortBy.dir}
                  onSort={() => handleSort(h)}
                  testid={`${testIdPrefix}-sort-${h}`}
                />
              ))}
            </tr>
            <tr className="bg-white border-b">
              {headers.map(h => {
                const t = colTypes[h];
                const cur = filters[h];
                if (t === 'number') {
                  return (
                    <FilterNumRangeCell
                      key={h}
                      minVal={cur?.min ?? ''}
                      maxVal={cur?.max ?? ''}
                      onMin={(v) => setColFilter(h, { min: v })}
                      onMax={(v) => setColFilter(h, { max: v })}
                      testid={`${testIdPrefix}-fnum-${h}`}
                    />
                  );
                }
                if (t === 'date') {
                  return (
                    <FilterDateRangeCell
                      key={h}
                      minVal={cur?.dateMin ?? ''}
                      maxVal={cur?.dateMax ?? ''}
                      onMin={(v) => setColFilter(h, { dateMin: v })}
                      onMax={(v) => setColFilter(h, { dateMax: v })}
                      testid={`${testIdPrefix}-fdate-${h}`}
                    />
                  );
                }
                return (
                  <FilterTextCell
                    key={h}
                    value={typeof cur === 'string' ? cur : ''}
                    onChange={(v) => setColFilter(h, v)}
                    testid={`${testIdPrefix}-ftxt-${h}`}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr><td colSpan={Math.max(headers.length, 1)} className="px-4 py-8 text-center text-gray-500">{emptyMessage}</td></tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="px-4 py-6 text-center text-gray-500 text-xs italic">
                  No rows match the current filters.{' '}
                  <button onClick={clearFilters} className="text-blue-700 underline hover:no-underline">Clear filters</button>
                </td>
              </tr>
            ) : sorted.map((row, idx) => {
              const sidKey = row['Staff ID'] || row.Staff;
              const isRepeat = repeatSidSet && repeatSidSet.has(sidKey);
              return (
                <tr
                  key={`row-${row.id ?? row.Date ?? ''}-${row.Staff ?? row.Name ?? ''}-${idx}`}
                  className={`hover:bg-gray-50 ${isRepeat ? 'bg-red-50/40' : ''}`}
                  data-testid={`${testIdPrefix}-row-${idx}`}
                >
                  {headers.map(h => (
                    <td key={h} className="px-4 py-2 text-gray-700">
                      {h === 'Staff' && isRepeat && (
                        <span
                          className="inline-flex items-center gap-1 bg-red-100 text-red-700 border border-red-200 rounded-full px-1.5 py-0.5 text-[10px] font-semibold mr-2 align-middle"
                          title={repeatBadgeTitle}
                          data-testid={`${testIdPrefix}-repeat-badge-${idx}`}
                        >
                          🚩
                        </span>
                      )}
                      {row[h] === null || row[h] === undefined || row[h] === '' ? '—' : String(row[h])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortHeader({ label, isActive, dir, onSort, testid }) {
  const Icon = !isActive ? ArrowUpDown : (dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      onClick={onSort}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={11} className={isActive ? 'text-blue-600' : 'text-gray-300'} />
      </span>
    </th>
  );
}

function FilterTextCell({ value, onChange, testid }) {
  return (
    <th className="px-2 py-1.5 font-normal">
      <div className="relative">
        <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="filter…"
          className="w-full pl-5 pr-1.5 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-blue-300"
          data-testid={testid}
        />
      </div>
    </th>
  );
}

function FilterNumRangeCell({ minVal, maxVal, onMin, onMax, testid }) {
  return (
    <th className="px-1 py-1.5 font-normal">
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={minVal}
          onChange={(e) => onMin(e.target.value)}
          placeholder="min"
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-blue-300 tabular-nums"
          data-testid={`${testid}-min`}
        />
        <input
          type="number"
          value={maxVal}
          onChange={(e) => onMax(e.target.value)}
          placeholder="max"
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-blue-300 tabular-nums"
          data-testid={`${testid}-max`}
        />
      </div>
    </th>
  );
}

function FilterDateRangeCell({ minVal, maxVal, onMin, onMax, testid }) {
  return (
    <th className="px-1 py-1.5 font-normal">
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={minVal}
          onChange={(e) => onMin(e.target.value)}
          className="w-full px-1 py-1 text-[10px] border border-gray-200 rounded focus:outline-none focus:border-blue-300"
          data-testid={`${testid}-min`}
        />
        <input
          type="date"
          value={maxVal}
          onChange={(e) => onMax(e.target.value)}
          className="w-full px-1 py-1 text-[10px] border border-gray-200 rounded focus:outline-none focus:border-blue-300"
          data-testid={`${testid}-max`}
        />
      </div>
    </th>
  );
}
